import { createTheme, alpha, Shadows } from '@mui/material/styles';

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    highlighted: true;
  }
}
declare module '@mui/material/styles' {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteColor extends ColorRange {}

  interface Palette {
    baseShadow: string;
  }
}

const defaultTheme = createTheme();

// const customShadows: Shadows = [...defaultTheme.shadows];

export const brand = {
  50: 'hsl(210, 100%, 95%)',
  100: 'hsl(210, 100%, 92%)',
  200: 'hsl(210, 100%, 80%)',
  300: 'hsl(210, 100%, 65%)',
  400: 'hsl(210, 98%, 48%)',
  500: 'hsl(210, 98%, 42%)',
  600: 'hsl(210, 98%, 55%)',
  700: 'hsl(210, 100%, 35%)',
  800: 'hsl(210, 100%, 16%)',
  900: 'hsl(210, 100%, 21%)',
};

export const gray = {
  50: 'hsl(220, 35%, 97%)',
  100: 'hsl(220, 30%, 94%)',
  200: 'hsl(220, 20%, 88%)',
  300: 'hsl(220, 20%, 80%)',
  400: 'hsl(220, 20%, 65%)',
  500: 'hsl(220, 20%, 42%)',
  600: 'hsl(220, 20%, 35%)',
  700: 'hsl(220, 20%, 25%)',
  800: 'hsl(220, 30%, 6%)',
  900: 'hsl(220, 35%, 3%)',
};

export const green = {
  50: 'hsl(120, 80%, 98%)',
  100: 'hsl(120, 75%, 94%)',
  200: 'hsl(120, 75%, 87%)',
  300: 'hsl(120, 61%, 77%)',
  400: 'hsl(120, 44%, 53%)',
  500: 'hsl(120, 59%, 30%)',
  600: 'hsl(120, 70%, 25%)',
  700: 'hsl(120, 75%, 16%)',
  800: 'hsl(120, 84%, 10%)',
  900: 'hsl(120, 87%, 6%)',
};

export const orange = {
  50: 'hsl(45, 100%, 97%)',
  100: 'hsl(45, 92%, 90%)',
  200: 'hsl(45, 94%, 80%)',
  300: 'hsl(45, 90%, 65%)',
  400: 'hsl(45, 90%, 40%)',
  500: 'hsl(45, 90%, 35%)',
  600: 'hsl(45, 91%, 25%)',
  700: 'hsl(45, 94%, 20%)',
  800: 'hsl(45, 95%, 16%)',
  900: 'hsl(45, 93%, 12%)',
};

export const red = {
  50: 'hsl(0, 100%, 97%)',
  100: 'hsl(0, 92%, 90%)',
  200: 'hsl(0, 94%, 80%)',
  300: 'hsl(0, 90%, 65%)',
  400: 'hsl(0, 90%, 40%)',
  500: 'hsl(0, 90%, 30%)',
  600: 'hsl(0, 91%, 25%)',
  700: 'hsl(0, 94%, 18%)',
  800: 'hsl(0, 95%, 12%)',
  900: 'hsl(0, 93%, 6%)',
};

export const colorSchemes = {
  light: {
    palette: {
      primary: {
        light: 'hsl(220 81.8% 95.7%)',
        main: 'hsl(220 100% 52.9%)',
        dark: brand[700],
        contrastText: brand[50],
      },
      info: {
        light: brand[100],
        main: brand[300],
        dark: brand[600],
        contrastText: gray[50],
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
      },
      success: {
        light: green[300],
        main: '#B8E986',
        dark: green[800],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[300], 0.4),
      background: {
        default: 'hsl(0, 0%, 99%)',
        paper: 'hsl(220, 35%, 97%)',
      },
      text: {
        primary: 'hsl(221 100% 31.4%)',
        secondary: gray[600],
        warning: orange[400],
      },
      action: {
        hover: alpha(gray[200], 0.2),
        selected: `${alpha(gray[200], 0.3)}`,
      },
      baseShadow:
        'hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px',
    },
  },
  // dark: {
  //   palette: {
  //     primary: {
  //       contrastText: brand[50],
  //       light: brand[300],
  //       main: brand[400],
  //       dark: brand[700],
  //     },
  //     info: {
  //       contrastText: brand[300],
  //       light: brand[500],
  //       main: brand[700],
  //       dark: brand[900],
  //     },
  //     warning: {
  //       light: orange[400],
  //       main: orange[500],
  //       dark: orange[700],
  //     },
  //     error: {
  //       light: red[400],
  //       main: red[500],
  //       dark: red[700],
  //     },
  //     success: {
  //       light: green[400],
  //       main: green[500],
  //       dark: green[700],
  //     },
  //     grey: {
  //       ...gray,
  //     },
  //     divider: alpha(gray[700], 0.6),
  //     background: {
  //       default: gray[900],
  //       paper: 'hsl(220, 30%, 7%)',
  //     },
  //     text: {
  //       primary: 'hsl(0, 0%, 100%)',
  //       secondary: gray[400],
  //     },
  //     action: {
  //       hover: alpha(gray[600], 0.2),
  //       selected: alpha(gray[600], 0.3),
  //     },
  //     baseShadow:
  //       'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px',
  //   },
  // },
};

export const typography = {
  fontFamily: 'Nivea, sans-serif',
  fontSize: 12,

  h1: {
    fontSize: defaultTheme.typography.pxToRem(48),
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: -0.5,
    textTransform: 'uppercase',
  },
  h2: {
    fontSize: defaultTheme.typography.pxToRem(36),
    fontWeight: 600,
    lineHeight: 1.2,
    textTransform: 'uppercase',
  },
  h3: {
    fontSize: defaultTheme.typography.pxToRem(30),
    fontWeight: 600,
    lineHeight: 1.2,
    textTransform: 'uppercase',
  },
  h4: {
    fontSize: defaultTheme.typography.pxToRem(24),
    fontWeight: 600,
    lineHeight: 1.5,
    textTransform: 'uppercase',
  },
  h5: {
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  h6: {
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 600,
    letterSpacing: '-0.45px',
    textTransform: 'uppercase',
  },
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 500,
  },
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 500,
  },
  // body1: {
  //   fontSize: defaultTheme.typography.pxToRem(14),
  // },
  // body2: {
  //   fontSize: defaultTheme.typography.pxToRem(14),
  //   fontWeight: 400,
  // },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 600,
  },
  label: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 600,
    lineHeight: defaultTheme.typography.pxToRem(24),
  },
};

declare module '@mui/material/styles' {
  interface TypographyVariants {
    label: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    label?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    label: true;
  }
}

export const shape = {
  borderRadius: 4,
};

export const spacing = {
  spacing: 4,
};

export const breakpoints = {
  values: {
    xs: 0,
    sm: 480,
    md: 960,
    lg: 1200,
    xl: 1600,
  },
};

// @ts-expect-error: Custom shadow array type mismatch
const defaultShadows: Shadows = [
  'none',
  'var(--npt-palette-baseShadow)',
  ...defaultTheme.shadows.slice(2),
];
export const shadows = defaultShadows;

export const customSpacing = {
  xs: {
    xs: 1,
    sm: 1,
    md: 1,
    lg: 1,
    xl: 1,
  },
  s: {
    xs: 2,
    sm: 2,
    md: 2,
    lg: 2,
    xl: 2,
  },
  m: {
    xs: 4,
    sm: 4,
    md: 4,
    lg: 4,
    xl: 4,
  },
  l: {
    xs: 6,
    sm: 6,
    md: 6,
    lg: 6,
    xl: 6,
  },
  xl: {
    xs: 4,
    sm: 4,
    md: 8,
    lg: 8,
    xl: 8,
  },
  xxl: {
    xs: 8,
    sm: 10,
    md: 20,
    lg: 20,
    xl: 20,
  },
  xxxl: {
    xs: 16,
    sm: 20,
    md: 40,
    lg: 40,
    xl: 40,
  },
};

// type CustomTheme = {
//   [Key in keyof typeof baseTheme]: (typeof baseTheme)[Key];
// };

// Extending the MUI theme with customSpacing
declare module '@mui/material/styles' {
  interface Theme {
    customSpacing: {
      s: {
        xs: number;
        sm: number;
        md: number;
        lg: number;
        xl: number;
      };
      m: {
        xs: number;
        sm: number;
        md: number;
        lg: number;
        xl: number;
      };
      l: {
        xs: number;
        sm: number;
        md: number;
        lg: number;
        xl: number;
      };
      xl: {
        xs: number;
        sm: number;
        md: number;
        lg: number;
        xl: number;
      };
      xxl: {
        xs: number;
        sm: number;
        md: number;
        lg: number;
        xl: number;
      };
      xxxl: {
        xs: number;
        sm: number;
        md: number;
        lg: number;
        xl: number;
      };
    };
  }

  // Allow configuration using `createTheme`
  interface ThemeOptions {
    customSpacing?: {
      s?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
      };
      m?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
      };
      l?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
      };
      xl?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
      };
      xxl?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
      };
      xxxl?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
      };
    };
  }
}
